import {inject, Injectable} from '@angular/core';
import {
  GetSelectDataParams,
  RecordData, RecordUpdateRequestParams,
  SelectData
} from "../../../../types/records-table.types";
import {AttachProps, DetachProps} from "../../../../types/attach-detach.types";
import {contactResourceType, propertyLazySelectAccountTgIds} from "../configs/contacts-modal-config";
import {BaseModalService} from "../../services/base-modal.service";
import {ContactsModalApiService} from "../services-api/contacts-modal-api.service";
import {ContactDataInTable, CreateContactDataParams} from "../types/contacts-modal.types";
import {RecordTableApiService} from "../../../../services-api/record-table-api.service";
import {BaseModalApiService} from "../../services/base-modal-api.service";
import {GetRecordPropertyDataByIdParams} from "../../base-modal/types/base-modal.types";

@Injectable({
  providedIn: 'root'
})
export class ContactsModalService {
  private baseModalService: BaseModalService = inject(BaseModalService);
  private contactsModalApiService: ContactsModalApiService = inject(ContactsModalApiService);
  private recordTableApiService: RecordTableApiService = inject(RecordTableApiService);
  private baseModalApiService: BaseModalApiService = inject(BaseModalApiService);

  public createAttachProps(recordCalendarId: number, bloggerContactId: number): AttachProps {
    return {
      recordCalendarId: recordCalendarId,
      id: bloggerContactId,
      path: contactResourceType.path
    };
  }

  public getDetachProps(recordData: RecordData): DetachProps {
    return {
      path: contactResourceType.path,
      recordCalendarId: this.baseModalService.getRecordCalendarId(recordData)
    }
  };

  public createContactData(params: CreateContactDataParams): Promise<RecordData> {
    return new Promise((resolve, reject) => {
      this.contactsModalApiService.createContactData(params).subscribe({
        next: (bankDetailsData: RecordData) => resolve(bankDetailsData),
        error: (error) => reject(error)
      });
    });
  }

  public updateBloggerContact(recordData: RecordData, bloggerContactId: number | null): ContactDataInTable {
    const bloggerId = (recordData['bloggerContact'] as RecordData)['bloggerId'];

    return {
      id: bloggerContactId,
      bloggerId: Number(bloggerId),
      valid: !!bloggerContactId
    };
  }

  public fetchTelegramAccountIds(recordData: RecordData): Promise<Array<SelectData>> {
    const recordCalendarId = this.baseModalService.getRecordCalendarId(recordData);
    const getSelectDataParams: GetSelectDataParams = {
      propertyLazySelect: propertyLazySelectAccountTgIds,
      id: recordCalendarId
    };

    return new Promise((resolve, reject) => {
      this.recordTableApiService.fetchSelectData(getSelectDataParams).subscribe({
        next: (selectData: Array<SelectData>) => resolve(selectData),
        error: (error) => reject(error)
      });
    })
  }

  public fetchContactsData(bloggerId: number | null): Promise<Array<RecordData>> {
    const params = {
      page: 0,
      size: undefined,
      bloggerId: bloggerId
    };

    return new Promise((resolve, reject) => {
      this.contactsModalApiService.fetchContactsData(params).subscribe({
        next: (data) => resolve(data),
        error: (error) => reject(error)
      });
    });
  }

  public fetchContactDataById<T>(getRecordPropertyDataByIdParams: GetRecordPropertyDataByIdParams): Promise<T> {
    return new Promise((resolve, reject) => {
      this.baseModalApiService.fetchDataById<T>(getRecordPropertyDataByIdParams).subscribe({
        next: (recordPropertyData: T) => resolve(recordPropertyData),
        error: (error) => reject(error)
      });
    });
  }

  public updateContactData(params: RecordUpdateRequestParams): Promise<RecordData> {
    return new Promise((resolve, reject) => {
      this.recordTableApiService.updateRecord<RecordData>(params).subscribe({
        next: (data) => resolve(data),
        error: (error) => reject(error)
      });
    })
  }

  public attachData(attachProps: AttachProps): Promise<void> {
    return new Promise((resolve, reject) => {
      this.baseModalApiService.attachRecordData(attachProps).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      });
    });
  }

  public detachContactData(detachProps: DetachProps): Promise<void> {
    return new Promise((resolve, reject) => {
      this.baseModalApiService.detachRecordData(detachProps).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      });
    });
  }

  public deleteData(path: string, recordId: string | number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.recordTableApiService.deleteRecord(path, recordId).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      });
    });
  }
}
